import type { TestFeedbackElementValue } from '@vedalib/editor/lib/elements'
import { TestFeedbackType } from '@vedalib/editor/lib/elements'
import { useMemo } from 'react'
import { chainName } from 'utils'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { FieldSegmented, FieldSelect, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { Typography } from 'components/uiKit/Typography'
import type { Block } from 'services/Store/Project/types'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import type { TestFeedbackValueFormType } from './testFeedbackTypes'

const useShowFeedback = ({ name }: IFieldConfig<Block>) => {
  const value = useFormData<TestFeedbackElementValue>(name, 1)
  const hidden = !value?.show
  return useMemo(() => ({ hidden }), [hidden])
}

const SHOW = genElementValueField({
  as: FieldSegmented<boolean>,
  name: 'show',
  label: t('elements.testFeedback.form.feedback'),
  defaultValue: false,
})

const TYPE = genElementValueField({
  as: FieldSelect<TestFeedbackType>,
  name: 'type',
  label: t('input.label.type'),
  props: {
    fluid: true,
    placeholder: t('input.placeholder.chooseType'),
    options: [
      getEnumOption('TestFeedbackType', TestFeedbackType.validation),
      getEnumOption('TestFeedbackType', TestFeedbackType.info),
    ],
  },
  useConfigs: [useShowFeedback],
})

const TestFeedbackForm: TestFeedbackValueFormType = ({ element, name }) => {
  const hintText =
    element.value.type === 'info'
      ? t('elements.testFeedback.form.infoHint')
      : t('elements.testFeedback.form.validationHint')

  return (
    <Padding bottom gap sides>
      <FormField config={chainName(name, SHOW)} />
      <FormField config={chainName(name, TYPE)} />
      <Typography styleType='hint'>{hintText}</Typography>
    </Padding>
  )
}

export default TestFeedbackForm
